import React, { FC, useState } from 'react';
import moment, { Moment } from 'moment';
import { useRouter } from 'shared-routing';
import { SelectOptionType } from 'shared-types/Common';

import { useTranslations } from '@lang';
import { parseDateToString } from '@utils/helpers';

import { EntitiesListingProps } from './EntitiesListing.types';

import { DateCategoryPicker } from '../JourneyPicker';
import Markdown from '@common/components/Markdown';
import { ProductGrid } from '@common/components/ProductGrid';
import SectionContainer from '@common/components/SectionContainer';
import { Tabs, Tab } from '@common/components/Tabs';
import { Title1 } from '@common/components/Typography';

import {
  StyledPageDescription,
  ProductGridWrapper,
  EntitiesListingWrapper,
  JourneyPickerInnerWrapper,
  JourneyPickerOuterWrapper,
} from './EntitiesListings.styles';
import { filterNotAvailableEntities } from './utils';

export const EntitiesListing: FC<EntitiesListingProps> = ({
  entities,
  entitiesAll = [],
  entitiesCategories,
  entitiesPage,
  entityRoute,
  defaultTitle = 'spa.spaItems',
  journeyParams,
  listingRoute,
  changeJourneyParam,
  setCategoryFilterId,
  categoryFilterId = null,
}) => {
  const { t } = useTranslations();
  const router = useRouter();
  const { arrivalDate } = router.query;
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    categoryFilterId ? String(categoryFilterId) : null
  );

  const [tabInitialDate, setInitialTabDate] = useState(
    (arrivalDate as string) || journeyParams?.fromDate
  );
  const [pickerDate, setPickerDate] = useState((arrivalDate as string) || journeyParams?.fromDate);

  const handleCategoryChange = ({ value }: SelectOptionType): void => {
    const newValue = value ? String(value) : null;
    setSelectedCategory(newValue);
  };
  const [tabDate, setTabDate] = useState((arrivalDate as string) || journeyParams?.fromDate);

  const updateUrl = ({ date, category }) => {
    const params = {
      category: category ? category : '',
      arrivalDate: parseDateToString(date),
    };
    router.push(
      {
        pathname: listingRoute,
        query: params as any,
      },
      undefined,
      { shallow: true }
    );
  };

  const updateJourneyParams = ({ date, category }) => {
    changeJourneyParam('spaDate', date);
    changeJourneyParam('category', category);
  };

  const handleTabChange = async (selectedDate) => {
    await setTabDate(selectedDate);
    await updateUrl({ date: selectedDate, category: selectedCategory });

    updateJourneyParams({ date: selectedDate, category: selectedCategory });
  };

  const handleUpdate = () => {
    setInitialTabDate(pickerDate);
    setTabDate(pickerDate);
    updateUrl({ date: pickerDate, category: selectedCategory });
    if (String(selectedCategory) !== String(categoryFilterId)) {
      setCategoryFilterId(
        !selectedCategory || isNaN(Number(selectedCategory)) ? null : Number(selectedCategory)
      );
    }
    updateJourneyParams({ date: pickerDate, category: selectedCategory });
  };

  const allCategoriesOption = {
    label: t('common.allCategories'),
    value: null,
  };

  const categoryOptions = [
    allCategoriesOption,
    ...entitiesCategories.map((category) => ({
      label: category.name,
      value: category.id,
    })),
  ];

  const notAvailablEntities = filterNotAvailableEntities(entitiesAll, entities);

  const currentSelectedCategory = categoryOptions.find(
    ({ value }) => String(value) === String(selectedCategory)
  );

  const weekDays = Array.from({
    length: 7,
  }).map((_, index) => moment(tabInitialDate).add(index, 'days'));

  const entitiesList = [...entities, ...notAvailablEntities];

  const entitiesListWithCategory = categoryFilterId
    ? entitiesList.filter(({ categories }) =>
        categories.find((category) => category.id === categoryFilterId)
      )
    : entitiesList;

  return (
    <EntitiesListingWrapper>
      <JourneyPickerOuterWrapper>
        <JourneyPickerInnerWrapper>
          <DateCategoryPicker
            fromDate={moment(pickerDate)}
            onDateChange={setPickerDate}
            categories={categoryOptions}
            onCategoryChange={handleCategoryChange}
            selectedCategory={currentSelectedCategory}
            handleUpdate={handleUpdate}
          />
        </JourneyPickerInnerWrapper>
      </JourneyPickerOuterWrapper>
      <Tabs selectedIndex={weekDays.findIndex((weekDay) => moment(weekDay).isSame(tabDate, 'day'))}>
        {weekDays.map((date, index) => (
          <Tab
            key={index}
            label={moment(date).format('ddd D MMM')}
            value={date.format()}
            onClick={handleTabChange}
          >
            <SectionContainer>
              <Title1 tag="h2">{entitiesPage?.title || t(defaultTitle)}</Title1>
              {entitiesPage?.content && (
                <StyledPageDescription>
                  <Markdown text={entitiesPage?.content} />
                </StyledPageDescription>
              )}
              <ProductGridWrapper>
                <ProductGrid
                  columns={3}
                  route={entityRoute}
                  items={entitiesListWithCategory}
                  hideOptionsRow
                  showInfoIfNoItems={false}
                  date={tabDate as Moment}
                />
              </ProductGridWrapper>
            </SectionContainer>
          </Tab>
        ))}
      </Tabs>
    </EntitiesListingWrapper>
  );
};
